import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { restClient } from './RestClient';

interface ForgotPasswordProps {
  onSwitchToLogin: () => void;
}

export function ForgotPassword({ onSwitchToLogin }: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);

  const addError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 8000);
  };

  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      addError('Please enter your email address');
      return;
    }

    const emailRegex = /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      addError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const result = await restClient.forgotPassword(email.trim());

      if (result.IsSuccess) {
        setIsSubmitted(true);
      } else {
        addError(result.Error.description || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      addError('Unable to connect to server. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ErrorNotification errors={errors} onDismiss={dismissError} />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo auth-logo-login">
            <KeyRound size={28} />
          </div>
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">
            {isSubmitted
              ? 'Check your email'
              : 'Enter your email to receive a reset link'}
          </p>
        </div>

        {isSubmitted ? (
          <div className="mb-6">
            <div className="alert alert-success">
              If an account with that email exists, a password reset link has been sent. Please check your inbox.
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="form-group-lg">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={isLoading}
                className="form-control form-control-lg"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`btn btn-lg btn-full ${isLoading ? 'btn-secondary' : 'btn-primary'}`}
            >
              {isLoading ? (
                <>
                  <span className="spinner" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p className="auth-footer-text">Remember your password?</p>
          <button
            onClick={onSwitchToLogin}
            disabled={isLoading}
            className="btn-link"
            style={{ opacity: isLoading ? 0.5 : 1 }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
